import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register, getOrganizationsList, checkOrgName } from '../services/api';
import { sendOTP, verifyOTP, acceptTerms } from '../services/api';
import SignupMobile from './SignupMobile';

const Signup = () => {
  const navigate = useNavigate();

  // null = Application type selection screen
  // 'accreditation' = Accreditation / Re-accreditation process
  // 'offcampus' = Off-Campus Activity process
  const [applicationType, setApplicationType] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    orgName: '',
    acronym: '',
    position: 'President',
    activityName: '',
    f11Code: '',
    orgCategory: '',
    presidentContact: '',
    adviserName: '',
    adviserContact: '',
    totalMembers: '',
  });

  // 'new' = New Organization (Accreditation)
  // 'existing' = Existing Org Handover (Re-accreditation)
  const [registrationType, setRegistrationType] = useState('new');

  // Org list for the handover dropdown
  const [orgList, setOrgList] = useState([]);
  const [orgListLoading, setOrgListLoading] = useState(false);
  const [selectedExistingOrg, setSelectedExistingOrg] = useState(null); // { organization_id, name, acronym, college }

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [collegeType, setCollegeType] = useState('');
  const [collegeValue, setCollegeValue] = useState('');

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const [showOTPStep, setShowOTPStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsError, setTermsError] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch org list when the user switches to the handover option
  useEffect(() => {
    if (applicationType === 'accreditation' && registrationType === 'existing' && orgList.length === 0) {
      setOrgListLoading(true);
      getOrganizationsList()
        .then(res => setOrgList(res.data))
        .catch(() => setOrgList([]))
        .finally(() => setOrgListLoading(false));
    }
  }, [applicationType, registrationType, orgList.length]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegistrationTypeChange = (type) => {
    setRegistrationType(type);
    setError('');
    setSelectedExistingOrg(null);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      setTermsError('You must agree to the Terms and Conditions before registering.');
      return;
    }
    setTermsError('');

    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Password and Confirm Password do not match.');
      return;
    }

    if (applicationType === 'offcampus') {
      if (!formData.activityName.trim()) {
        setError('Please enter your activity name.');
        return;
      }
      if (!collegeValue) {
        setError('Please select a college or campus.');
        return;
      }
    } else {
      if (registrationType === 'new') {
        if (!collegeValue) {
          setError('Please select a college or campus.');
          return;
        }
        if (!formData.orgName.trim()) {
          setError('Please enter your organization name.');
          return;
        }

        // Check if org name already exists before sending OTP
        try {
          const checkRes = await checkOrgName(formData.orgName.trim());
          if (checkRes.data.exists) {
            setError('An organization with that name already exists. Please use a different name.');
            return;
          }
        } catch (err) {
          // If check fails, proceed — backend will catch it
        }
      }

      if (registrationType === 'existing') {
        if (!selectedExistingOrg) {
          setError('Please select your organization from the list.');
          return;
        }
      }
    }

    const passwordRegex = /^(?=.*[A-Z])(?=.*[0-9]).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long.');
      } else if (!/[A-Z]/.test(formData.password)) {
        setError('Password must contain at least one uppercase letter.');
      } else if (!/[0-9]/.test(formData.password)) {
        setError('Password must contain at least one number.');
      }
      return;
    }

    setLoading(true);
    try {
      await sendOTP(formData.email, 'registration', applicationType);
      setShowOTPStep(true);
      setOtpError('');
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code.');
    } finally {
      setLoading(false);
    }
    return;
  };

  const handleSubmitRegistration = async () => {
    setLoading(true);
    try {
      const payload =
        applicationType === 'offcampus'
          ? {
            full_name: formData.fullName,
            slsu_email: formData.email,
            password: formData.password,
            application_type: 'offcampus',
            activity_name: formData.activityName,
            college: collegeValue,
          }
          : registrationType === 'existing'
            ? {
              full_name: formData.fullName,
              slsu_email: formData.email,
              password: formData.password,
              application_type: 'accreditation',
              org_type: 'existing',
              existing_org_id: selectedExistingOrg.organization_id,
              president_contact: formData.presidentContact || null,
              adviser_name: formData.adviserName || null,
              adviser_contact: formData.adviserContact || null,
              total_members: formData.totalMembers ? parseInt(formData.totalMembers, 10) : null,
            }
            : {
              full_name: formData.fullName,
              slsu_email: formData.email,
              password: formData.password,
              application_type: 'accreditation',
              org_type: 'new',
              organization_name: formData.orgName,
              organization_acronym: formData.acronym || null,
              organization_college: collegeValue,
              organization_category: formData.orgCategory || null,
              president_contact: formData.presidentContact || null,
              adviser_name: formData.adviserName || null,
              adviser_contact: formData.adviserContact || null,
              total_members: formData.totalMembers ? parseInt(formData.totalMembers, 10) : null,
            };

      await register(payload);
      // Persist the email so the Login page can offer a quick status-check link
      localStorage.setItem('orgpass_pending_email', formData.email);
      navigate(`/request-status?status=pending&email=${encodeURIComponent(formData.email)}&application_type=${encodeURIComponent(applicationType)}`);
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Failed to submit your request. Please check your connection and try again.';
      setError(errMsg);
      throw new Error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpLoading(true);
    try {
      await verifyOTP(formData.email, otpCode, 'registration');
      await handleSubmitRegistration();
    } catch (err) {
      setOtpError(err.response?.data?.detail || err.message || 'Invalid code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const collegeOptions = {
    College: ['CABHA', 'CAG', 'CAM', 'CAS', 'CEN', 'CIT', 'CTE', 'Graduate School', 'SRSCO'],
    Campus: ['Alabat', 'Catanauan', 'Gumaca', 'Infanta', 'Lucena', 'Polillo', 'Tayabas', 'Tiaong', 'Tagkawayan'],
  };

  const isFormValid =
    formData.fullName &&
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    (applicationType === 'offcampus'
      ? formData.activityName && collegeValue
      : (registrationType === 'new'
        ? formData.orgName && collegeValue
        : selectedExistingOrg !== null));

  if (isMobile) {
    return (
      <SignupMobile
        formData={formData}
        handleChange={handleChange}
        error={error}
        handleSignUp={handleSignUp}
        collegeType={collegeType}
        setCollegeType={setCollegeType}
        collegeValue={collegeValue}
        setCollegeValue={setCollegeValue}
        loading={loading}
        isFormValid={isFormValid}
        collegeOptions={collegeOptions}
        applicationType={applicationType}
        setApplicationType={setApplicationType}
        registrationType={registrationType}
        onRegistrationTypeChange={handleRegistrationTypeChange}
        orgList={orgList}
        orgListLoading={orgListLoading}
        selectedExistingOrg={selectedExistingOrg}
        setSelectedExistingOrg={setSelectedExistingOrg}
        showOTPStep={showOTPStep}
        setShowOTPStep={setShowOTPStep}
        otpCode={otpCode}
        setOtpCode={setOtpCode}
        otpLoading={otpLoading}
        setOtpLoading={setOtpLoading}
        otpError={otpError}
        setOtpError={setOtpError}
        handleVerifyAndRegister={handleVerifyAndRegister}
        agreedToTerms={agreedToTerms}
        setAgreedToTerms={setAgreedToTerms}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        termsError={termsError}
        setTermsError={setTermsError}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8"
      style={{
        background: '#054334',
        backgroundImage: 'url(assets/login_image.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Dark overlay on background */}
      <div className="absolute inset-0" style={{ background: 'rgba(6, 60, 40, 0.82)' }} />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[1200px] bg-white rounded-[40px] shadow-2xl overflow-hidden flex" style={{ height: '90vh', minHeight: '650px' }}>

        {/* Left Panel — Form */}
        <div className="flex flex-col justify-between items-center w-[60%] px-12 py-8 relative overflow-y-auto min-h-full">

          {/* Logo */}
          <div className="w-full flex justify-center items-center space-x-2 mb-4 mt-2">
            <img src="assets/osas-logo.png" alt="OrgPass" className="w-6 h-6" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-bold text-gray-800 text-[16px]">OrgPass</span>
          </div>

          <div className="w-full max-w-[600px] flex flex-col justify-center my-auto">
            {/* Error message */}
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[13px] font-medium text-center">
                {error}
              </div>
            )}

            {/* ── STEP 1: Application Type Selection Screen ── */}
            {applicationType === null ? (
              <div className="w-full flex flex-col items-center justify-center my-auto">
                <h1 className="text-[32px] font-extrabold text-gray-900 mb-28 text-center">Create your account</h1>

                <p className="text-[13px] font-bold text-gray-800 mb-8 text-center">
                  What type of access are you applying for?
                </p>

                <div className="w-full max-w-[340px] flex flex-col gap-6">
                  <button
                    type="button"
                    onClick={() => {
                      setApplicationType('accreditation');
                      setError('');
                    }}

                    className="w-full py-3.5 px-6 rounded-xl border border-green-300 bg-white text-gray-700 font-bold text-[14px] hover:border-[#086C53] hover:text-[#086C53] hover:bg-[#086C53]/5 transition-all shadow-sm flex items-center justify-center cursor-pointer"
                  >
                    Accreditation/Re-accreditation
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setApplicationType('offcampus');
                      setError('');
                    }}
                    className="w-full py-3.5 px-6 rounded-xl border border-green-300 bg-white text-gray-700 font-bold text-[14px] hover:border-[#086C53] hover:text-[#086C53] hover:bg-[#086C53]/5 transition-all shadow-sm flex items-center justify-center cursor-pointer"
                  >
                    Off-Campus Activity
                  </button>
                </div>

                <div className="w-full flex flex-col items-center mt-12 space-y-1.5">
                  <p className="text-[12px] text-gray-500 font-medium">
                    Already have an account?{' '}
                    <Link to="/login" className="text-[#086C53] font-bold hover:underline transition-colors">Login</Link>
                  </p>
                  <Link
                    to="/request-status"
                    className="text-[11px] text-gray-400 hover:text-[#086C53] font-medium transition-colors"
                  >
                    Already applied? Check your application status →
                  </Link>
                </div>
              </div>
            ) : applicationType === 'offcampus' ? (
              /* ── STEP 2A: Off-Campus Activity Form ── */
              <form onSubmit={handleSignUp} className="space-y-5">
                <div className="text-center mb-6">
                  <h1 className="text-[32px] font-extrabold text-gray-900 mb-1 text-center">Create your account</h1>
                  <p className="text-[13px] text-gray-500 text-center">Fill the necessary fields</p>
                </div>

                <div className="relative flex items-center justify-center mb-4">
                  <button
                    type="button"
                    onClick={() => { setApplicationType(null); setError(''); }}
                    className="absolute left-0 text-[12px] font-bold text-gray-400 hover:text-[#086C53] flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Back</span>
                  </button>
                  <span className="text-[13px] font-bold text-gray-700">Off-Campus Activity</span>
                </div>

                <div className="flex flex-col md:flex-row gap-8 md:gap-10">
                  {/* Left Column: Personal Information */}
                  <div className="flex-1 space-y-4">
                    <h3 className="font-bold text-gray-900 text-[14px] mb-2 border-b border-gray-100 pb-2">Personal Information</h3>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="@slsu.edu.ph"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        <span className="font-semibold text-gray-600">Note:</span> Satellite campuses may use @gmail.com if @slsu.edu.ph is unavailable.
                      </p>
                    </div>

                    <div className="relative">
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Password</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-8 text-gray-400 hover:text-[#086C53]"
                      >
                        {showPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        )}
                      </button>
                      <div className="mt-1.5 space-y-1">
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{formData.password.length >= 8 ? '✓' : '○'}</span>
                          At least 8 characters
                        </p>
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one uppercase letter
                        </p>
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${/[0-9]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one number
                        </p>
                      </div>
                    </div>

                    <div className="relative">
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Confirm Password</label>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-8 text-gray-400 hover:text-[#086C53]"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Right Column: Activity Information */}
                  <div className="flex-1 space-y-4">
                    <h3 className="font-bold text-gray-900 text-[14px] mb-2 border-b border-gray-100 pb-2">
                      Activity Information
                    </h3>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Activity Name</label>
                      <input
                        type="text"
                        name="activityName"
                        value={formData.activityName}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">College/Campus</label>
                      <div className="flex space-x-2">
                        <div className="relative flex-1">
                          <select
                            value={collegeType}
                            onChange={(e) => { setCollegeType(e.target.value); setCollegeValue(''); }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-xs appearance-none bg-white text-gray-700"
                          >
                            <option value="">College/Campus</option>
                            <option value="College">College</option>
                            <option value="Campus">Campus</option>
                          </select>
                        </div>
                        <div className="relative flex-1">
                          <select
                            value={collegeValue}
                            onChange={(e) => setCollegeValue(e.target.value)}
                            disabled={!collegeType}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-xs appearance-none bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                          >
                            <option value="">
                              {collegeType ? `Select ${collegeType}` : 'Select first'}
                            </option>
                            {collegeType && collegeOptions[collegeType].map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3 mt-2">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (e.target.checked) setTermsError('');
                    }}
                    className="mt-1 w-4 h-4 accent-[#086C53] cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="terms-checkbox" className="text-[13px] text-gray-600 leading-relaxed">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#086C53] font-bold underline hover:text-[#054334] transition-colors"
                    >
                      Terms and Conditions
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#086C53] font-bold underline hover:text-[#054334] transition-colors"
                    >
                      Privacy Policy
                    </button>
                    {' '}of OrgPass.
                  </label>
                </div>
                {termsError && (
                  <p className="text-red-500 text-[12px] font-medium mt-1">{termsError}</p>
                )}

                {/* Submit Button */}
                <div className="flex flex-col justify-center items-center pt-4 space-y-6 pb-2">
                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="flex items-center justify-center space-x-2 px-28 py-4 rounded-xl text-[14px] font-bold text-white transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed w-full md:w-auto"
                    style={{ background: '#054334' }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#065440')}
                    onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#054334')}
                  >
                    <span>{loading ? 'Submitting...' : 'Request Access'}</span>
                    {!loading && (
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    )}
                  </button>

                  {/* Footer */}
                  <div className="w-full flex flex-col items-center pb-2 space-y-1">
                    <p className="text-[12px] text-gray-500 font-medium">
                      Already have an account?{' '}
                      <Link to="/login" className="text-[#086C53] font-bold hover:underline transition-colors">Login</Link>
                    </p>
                    <Link
                      to="/request-status"
                      className="text-[11px] text-gray-400 hover:text-[#086C53] font-medium transition-colors"
                    >
                      Already applied? Check your application status →
                    </Link>
                  </div>
                </div>
              </form>
            ) : (
              /* ── STEP 2B: Accreditation / Re-accreditation Form ── */
              <form onSubmit={handleSignUp} className="space-y-5">
                <div className="text-center mb-6">
                  <h1 className="text-[32px] font-extrabold text-gray-900 mb-1 text-center">Create your account</h1>
                  <p className="text-[13px] text-gray-500 text-center">Fill the necessary fields</p>
                </div>

                <div className="flex items-center justify-between mb-1">
                  <button
                    type="button"
                    onClick={() => { setApplicationType(null); setError(''); }}
                    className="text-[12px] font-bold text-gray-400 hover:text-[#086C53] flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Change access type</span>
                  </button>
                </div>

                {/* Registration Type Selection */}
                <div className="mb-6">
                  <label className="block text-[13px] font-bold text-gray-800 mb-3 text-center">Re-accreditation or Accreditation?</label>

                  <div className="flex flex-col md:flex-row gap-4 justify-center">
                    {/* Existing Org (Handover) */}
                    <label
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${registrationType === 'existing'
                        ? 'border-[#086C53] bg-[#086C53]/5 text-[#086C53]'
                        : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'
                        }`}
                      onClick={() => handleRegistrationTypeChange('existing')}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-[13px] font-bold">Existing Organization</span>
                      {registrationType === 'existing' && (
                        <svg className="w-4 h-4 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </label>

                    {/* New Organization */}
                    <label
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${registrationType === 'new'
                        ? 'border-[#086C53] bg-[#086C53]/5 text-[#086C53]'
                        : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'
                        }`}
                      onClick={() => handleRegistrationTypeChange('new')}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="text-[13px] font-bold">New Organization</span>
                      {registrationType === 'new' && (
                        <svg className="w-4 h-4 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </label>
                  </div>

                  {/* Context hint */}
                  <p className="text-[11px] text-gray-400 text-center mt-2">
                    {registrationType === 'existing'
                      ? 'Select your organization and submit a handover access request. You will go through the same approval process.'
                      : 'Register your new organization. You will go through the accreditation process.'}
                  </p>
                </div>

                <div className="flex flex-col md:flex-row gap-8 md:gap-10">
                  {/* Left Column: Personal Information */}
                  <div className="flex-1 space-y-4">
                    <h3 className="font-bold text-gray-900 text-[14px] mb-2 border-b border-gray-100 pb-2 flex items-center min-h-[30px]">Personal Information</h3>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="@slsu.edu.ph"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        <span className="font-semibold text-gray-600">Note:</span> Satellite campuses may use @gmail.com if @slsu.edu.ph is unavailable.
                      </p>
                    </div>

                    <div className="relative">
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Password</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-8 text-gray-400 hover:text-[#086C53]"
                      >
                        {showPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        )}
                      </button>
                      <div className="mt-1.5 space-y-1">
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{formData.password.length >= 8 ? '✓' : '○'}</span>
                          At least 8 characters
                        </p>
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one uppercase letter
                        </p>
                        <p className={`text-[11px] font-medium flex items-center gap-1 ${/[0-9]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'
                          }`}>
                          <span>{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one number
                        </p>
                      </div>
                    </div>

                    <div className="relative">
                      <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Confirm Password</label>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-8 text-gray-400 hover:text-[#086C53]"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Right Column: Organization Information */}
                  <div className="flex-1 space-y-4">
                    <h3 className="font-bold text-gray-900 text-[14px] mb-2 border-b border-gray-100 pb-2 flex items-center justify-between gap-2">
                      <span className="whitespace-nowrap">Organization Information</span>
                      {registrationType === 'existing' && (
                        <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 normal-case whitespace-nowrap flex-shrink-0">
                          Re-accreditation
                        </span>
                      )}
                      {registrationType === 'new' && (
                        <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 normal-case whitespace-nowrap flex-shrink-0">
                          Accreditation
                        </span>
                      )}
                    </h3>

                    {registrationType === 'existing' ? (
                      /* ── Existing Org (Handover): just pick from dropdown ── */
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Select Your Organization</label>
                          {orgListLoading ? (
                            <div className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-400">
                              Loading organizations...
                            </div>
                          ) : (
                            <select
                              value={selectedExistingOrg ? selectedExistingOrg.organization_id : ''}
                              onChange={(e) => {
                                const org = orgList.find(o => o.organization_id === parseInt(e.target.value));
                                setSelectedExistingOrg(org || null);
                              }}
                              className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                              required
                            >
                              <option value="">— Select an organization —</option>
                              {orgList.map(org => (
                                <option key={org.organization_id} value={org.organization_id}>
                                  {org.name}{org.acronym ? ` (${org.acronym})` : ''}{org.college ? ` · ${org.college}` : ''}
                                </option>
                              ))}
                            </select>
                          )}
                          {orgList.length === 0 && !orgListLoading && (
                            <p className="text-[11px] text-gray-400 mt-1">No existing organizations found. Contact the admin if yours isn't listed.</p>
                          )}
                        </div>

                        {selectedExistingOrg && (
                          <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl px-4 py-3 space-y-1">
                            <p className="text-[12px] font-bold text-[#166534]">{selectedExistingOrg.name}</p>
                            {selectedExistingOrg.acronym && <p className="text-[11px] text-[#166534]">Acronym: {selectedExistingOrg.acronym}</p>}
                            {selectedExistingOrg.college && <p className="text-[11px] text-[#166534]">College/Campus: {selectedExistingOrg.college}</p>}
                            <p className="text-[10px] text-[#15803d] mt-1 font-medium">
                              ✓ This is a Re-accreditation request — 15 document requirements will apply.
                            </p>
                          </div>
                        )}

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Position</label>
                          <input
                            type="text"
                            name="position"
                            value={formData.position || 'President'}
                            readOnly
                            className="w-full bg-[#f1f5f9] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-700 font-medium cursor-not-allowed select-none focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Contact Number of Student Organization President</label>
                          <input
                            type="tel"
                            name="presidentContact"
                            placeholder="e.g. 09123456789"
                            value={formData.presidentContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Name of Student Organization Adviser</label>
                          <input
                            type="text"
                            name="adviserName"
                            placeholder="e.g. Prof. Juan Dela Cruz"
                            value={formData.adviserName}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Contact Number of Student Organization Adviser</label>
                          <input
                            type="tel"
                            name="adviserContact"
                            placeholder="e.g. 09123456789"
                            value={formData.adviserContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Total Number of Members</label>
                          <input
                            type="number"
                            name="totalMembers"
                            placeholder="e.g. 50"
                            min="1"
                            value={formData.totalMembers}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>
                      </div>
                    ) : (
                      /* ── New Organization: full form ── */
                      <>
                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Organization Name</label>
                          <input
                            type="text"
                            name="orgName"
                            value={formData.orgName}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Acronym (if any)</label>
                          <input
                            type="text"
                            name="acronym"
                            value={formData.acronym}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Type of Student Organization</label>
                          <select
                            name="orgCategory"
                            value={formData.orgCategory}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                          >
                            <option value="">— Select Type of Organization —</option>
                            <option value="Academic">Academic</option>
                            <option value="Civic">Civic (Non-Academic)</option>
                            <option value="Cultural">Cultural</option>
                            <option value="Religious">Religious</option>
                            <option value="Socio-Political">Socio-Political</option>
                            <option value="Sports">Sports</option>
                            <option value="Student Council">Student Council</option>
                            <option value="Others">Others</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">College/Campus</label>
                          <div className="flex space-x-2">
                            <div className="relative flex-1">
                              <select
                                value={collegeType}
                                onChange={(e) => { setCollegeType(e.target.value); setCollegeValue(''); }}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm appearance-none bg-white text-gray-700"
                              >
                                <option value="">College/Campus</option>
                                <option value="College">College</option>
                                <option value="Campus">Campus</option>
                              </select>
                            </div>
                            <div className="relative flex-1">
                              <select
                                value={collegeValue}
                                onChange={(e) => setCollegeValue(e.target.value)}
                                disabled={!collegeType}
                                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm appearance-none bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                              >
                                <option value="">
                                  {collegeType ? `Select ${collegeType}` : 'Select first'}
                                </option>
                                {collegeType && collegeOptions[collegeType].map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Position</label>
                          <input
                            type="text"
                            name="position"
                            value={formData.position || 'President'}
                            readOnly
                            className="w-full bg-[#f1f5f9] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-700 font-medium cursor-not-allowed select-none focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Contact Number of Student Organization President</label>
                          <input
                            type="tel"
                            name="presidentContact"
                            placeholder="e.g. 09123456789"
                            value={formData.presidentContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Name of Student Organization Adviser</label>
                          <input
                            type="text"
                            name="adviserName"
                            placeholder="e.g. Prof. Juan Dela Cruz"
                            value={formData.adviserName}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Contact Number of Student Organization Adviser</label>
                          <input
                            type="tel"
                            name="adviserContact"
                            placeholder="e.g. 09123456789"
                            value={formData.adviserContact}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>

                        <div>
                          <label className="block text-[12px] font-bold text-gray-800 mb-1.5">Total Number of Members</label>
                          <input
                            type="number"
                            name="totalMembers"
                            placeholder="e.g. 50"
                            min="1"
                            value={formData.totalMembers}
                            onChange={handleChange}
                            className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3 mt-2">
                  <input
                    type="checkbox"
                    id="terms-checkbox-accred"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (e.target.checked) setTermsError('');
                    }}
                    className="mt-1 w-4 h-4 accent-[#086C53] cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="terms-checkbox-accred" className="text-[13px] text-gray-600 leading-relaxed">
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#086C53] font-bold underline hover:text-[#054334] transition-colors"
                    >
                      Terms and Conditions
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#086C53] font-bold underline hover:text-[#054334] transition-colors"
                    >
                      Privacy Policy
                    </button>
                    {' '}of OrgPass.
                  </label>
                </div>
                {termsError && (
                  <p className="text-red-500 text-[12px] font-medium mt-1">{termsError}</p>
                )}

                {/* Submit Button */}
                <div className="flex flex-col justify-center items-center pt-4 space-y-6 pb-2">
                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="flex items-center justify-center space-x-2 px-28 py-4 rounded-xl text-[14px] font-bold text-white transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed w-full md:w-auto"
                    style={{ background: '#054334' }}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#065440')}
                    onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#054334')}
                  >
                    <span>{loading ? 'Submitting...' : 'Request Access'}</span>
                    {!loading && (
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    )}
                  </button>

                  {/* Footer */}
                  <div className="w-full flex flex-col items-center pb-2 space-y-1">
                    <p className="text-[12px] text-gray-500 font-medium">
                      Already have an account?{' '}
                      <Link to="/login" className="text-[#086C53] font-bold hover:underline transition-colors">Login</Link>
                    </p>
                    <Link
                      to="/request-status"
                      className="text-[11px] text-gray-400 hover:text-[#086C53] font-medium transition-colors"
                    >
                      Already applied? Check your application status →
                    </Link>
                  </div>
                </div>
              </form>
            )}

          </div>
        </div>

        {/* Right Panel — Image */}
        <div
          className="w-[40%] relative flex flex-col justify-start p-12 rounded-[32px] m-4 overflow-hidden"
          style={{
            backgroundColor: '#054334',
            backgroundImage: 'linear-gradient(to bottom, #004030 10%, rgba(0, 64, 38, 0) 60%), url(assets/login_image.png)',
            backgroundSize: '100% 100%, cover',
            backgroundPosition: 'top left, center',
            backgroundAttachment: 'scroll, fixed',
          }}
        >
          {/* Text */}
          <div className="relative z-10 ml-[-8px] mt-[-8px]">
            <p className="text-white text-[13px] font-bold uppercase tracking-[0.2em] mb-1">Welcome to</p>
            <h2 className="text-white text-[56px] font-extrabold leading-tight mb-3">OrgPass</h2>
            <p className="text-white/90 text-[14px] leading-relaxed max-w-[240px] font-medium">
              Official Website for OSAS Student Organization and Activities
            </p>
          </div>
        </div>

        {showOTPStep && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center rounded-[40px]">
            <div className="w-full max-w-md px-8 py-10 flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-[#e0f5ee] flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-[#054334]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-[24px] font-extrabold text-gray-900 mb-2 text-center">Verify your email</h2>
              <p className="text-[14px] text-gray-500 text-center mb-8">
                We sent a 6-digit code to <strong>{formData.email}</strong>. Enter it below to complete your registration.
              </p>
              {otpError && (
                <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[13px] font-medium text-center">
                  {otpError}
                </div>
              )}
              <form onSubmit={handleVerifyAndRegister} className="w-full space-y-6">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-5 py-4 text-[28px] font-bold text-center text-gray-800 tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                  required
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={otpLoading || otpCode.length !== 6}
                  className="w-full py-4 rounded-xl text-[16px] font-bold text-white transition-colors shadow-md disabled:opacity-60"
                  style={{ background: '#054334' }}
                >
                  {otpLoading ? 'Verifying...' : 'Verify & Complete Registration'}
                </button>
                <div className="flex flex-col items-center space-y-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await sendOTP(formData.email, 'registration', applicationType);
                        setOtpError('');
                        setOtpCode('');
                      } catch (err) {
                        setOtpError('Failed to resend code.');
                      }
                    }}
                    className="text-[13px] text-[#086C53] font-semibold hover:underline"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowOTPStep(false); setOtpCode(''); setOtpError(''); }}
                    className="text-[13px] text-gray-400 hover:text-gray-600 font-medium"
                  >
                    ← Go back and edit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>

      {showTermsModal && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h2 className="text-[20px] font-bold text-gray-900">Terms and Conditions & Privacy Policy</h2>
              <button onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 text-[13px] text-gray-700 leading-relaxed">

              <div>
                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">OrgPass — SLSU-OSAS</p>
                <p className="text-[11px] text-gray-400">Effective Date: Academic Year 2025-2026</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">1. Acceptance of Terms</h3>
                <p>By registering and using OrgPass, you agree to these Terms and Conditions. If you do not agree, do not use the system. These terms apply to all users including organization presidents, advisers, and OSAS administrators.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">2. Purpose of the System</h3>
                <p>OrgPass is an official document submission and validation system developed for SLSU-OSAS to digitize the student organization accreditation and off-campus activity approval process. It is intended solely for use by registered SLSU student organizations and OSAS staff.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">3. User Accounts and Eligibility</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Only users with a valid <strong>@slsu.edu.ph</strong> or <strong>@gmail.com</strong> email address may register</li>
                  <li>Each student organization may only have one registered account</li>
                  <li>Users are responsible for maintaining the confidentiality of their credentials</li>
                  <li>Accounts are non-transferable</li>
                  <li>OSAS reserves the right to suspend or terminate accounts that violate these terms</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">4. Document Submission</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Users are responsible for the accuracy and authenticity of all submitted documents</li>
                  <li>Submission of falsified, altered, or fraudulent documents is strictly prohibited and may result in account termination and referral to the appropriate SLSU disciplinary committee</li>
                  <li>Submitted documents must comply with OSAS requirements and CMO No. 63 s. 2017 for off-campus activities</li>
                  <li>Documents submitted through OrgPass are official submissions to SLSU-OSAS</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">5. Artificial Intelligence (AI) Document Validation</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>OrgPass uses Google Gemini AI to perform automated document validation</li>
                  <li>AI validation is assistive only — it flags potential issues and provides recommendations</li>
                  <li>Final review and approval decisions are made solely by OSAS administrators, not by the AI system</li>
                  <li>AI validation results may not be 100% accurate and should not be solely relied upon</li>
                  <li>By submitting documents, you consent to their analysis by Google Gemini AI for validation purposes</li>
                  <li>Since OrgPass uses the paid tier of the Google Gemini API, your submitted documents are NOT used to train Google's AI models</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">6. Data Collection and Use</h3>
                <p className="mb-2">We collect and process the following information:</p>
                <p className="font-semibold mb-1">Personal Information:</p>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <li>Full name, email address (@slsu.edu.ph or @gmail.com)</li>
                  <li>Organization name, college/campus</li>
                  <li>Adviser information and contact details</li>
                </ul>
                <p className="font-semibold mb-1">Document Data:</p>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <li>Submitted documents (PDF, DOCX, images)</li>
                  <li>Document metadata (submission date, status)</li>
                  <li>AI validation results</li>
                </ul>
                <p>This data is used exclusively for processing accreditation and off-campus activity applications, AI-powered document validation, communication regarding application status, and system administration by OSAS staff.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">7. Data Sharing with Third-Party Services</h3>
                <p className="mb-2">Your data may be shared with the following third-party services solely for system operation:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-semibold">Google Gemini AI</span> — Document validation (document content only)</li>
                  <li><span className="font-semibold">A cloud storage service</span> — Template file storage (template files only)</li>
                  <li><span className="font-semibold">An email delivery service</span> — Email notifications (email address only)</li>
                  <li><span className="font-semibold">A secure cloud server</span> — System hosting (all system data)</li>
                </ul>
                <p className="mt-2 text-[12px] text-gray-500 italic">We do not sell, rent, or share your personal data with any other third parties.</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">8. Data Storage and Security</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All submitted documents are stored on a secure cloud server</li>
                  <li>Data is transmitted using HTTPS/TLS encryption</li>
                  <li>Passwords are stored as cryptographic hash — never in plain text</li>
                  <li>Access to submitted documents is restricted to the submitting organization and authorized OSAS administrators</li>
                  <li>Authentication is required for all system access</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">9. Data Retention</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Submitted documents and user accounts are retained for the duration of the academic year</li>
                  <li>At the end of each academic year, accreditation documents and user accounts are deleted as part of the system's academic year reset</li>
                  <li>Organization records are retained for continuity across academic years</li>
                  <li>Off-campus activity documents are retained separately per OSAS policy</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">10. User Rights</h3>
                <p>As a registered user, you have the right to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>View all documents you have submitted</li>
                  <li>Cancel or delete unreviewed document submissions</li>
                  <li>Request correction of your personal information by contacting OSAS</li>
                  <li>Request deletion of your account by contacting OSAS</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">11. Prohibited Activities</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Submitting documents on behalf of another organization without authorization</li>
                  <li>Attempting to access other organizations' data</li>
                  <li>Using the system for any purpose other than SLSU-OSAS accreditation processes</li>
                  <li>Attempting to circumvent security measures</li>
                  <li>Uploading malicious files or content</li>
                  <li>Sharing account credentials with unauthorized persons</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">12. Limitation of Liability</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>OrgPass is provided as an official tool of SLSU-OSAS on an "as-is" basis</li>
                  <li>SLSU-OSAS is not liable for any technical issues, data loss, or service interruptions</li>
                  <li>AI validation results are advisory only — SLSU-OSAS is not liable for AI errors or inaccuracies</li>
                  <li>Users are solely responsible for the content and accuracy of submitted documents</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-[15px] mb-2">13. Contact Information</h3>
                <p>For questions, concerns, or data requests related to OrgPass:</p>
                <p className="mt-2 font-semibold">SLSU Office of Student Affairs and Services (OSAS)</p>
                <p>Southern Luzon State University</p>
                <p>Email: orgpass.slsu@gmail.com</p>
              </div>

            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-lg text-[13px] font-bold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowTermsModal(false);
                  setTermsError('');
                }}
                className="px-5 py-2 bg-[#086C53] text-white rounded-lg text-[13px] font-bold hover:bg-[#054334] transition-colors"
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;